import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Upload, 
  Loader2, 
  CheckCircle2, 
  AlertTriangle, 
  Image as ImageIcon,
  FileImage,
  ExternalLink,
  FolderOpen,
  Server,
  Settings
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface OptimizedImage {
  path: string;
  url: string;
  width: number;
  format: string;
  size: number;
}

interface OptimizationResult {
  success: boolean;
  images: OptimizedImage[];
  urls: Record<string, string>;
  sizes: Record<string, number>;
  message: string;
}

const IMAGE_TYPES = [
  { value: 'hero', label: 'Hero Image', description: 'Site-wide hero banners (16:9, up to 5K)' },
  { value: 'gallery', label: 'Gallery Image', description: 'Unit gallery images (16:9)' },
  { value: 'floor_plan', label: 'Floor Plan', description: 'Unit floor plans (variable ratio)' },
  { value: 'thumbnail', label: 'Thumbnail', description: 'Unit thumbnails (1:1 square)' },
  { value: 'stock', label: 'Stock Image', description: 'Shared property images (16:9)' },
  { value: 'exterior_render', label: 'Exterior Render', description: 'Architectural visualizations' },
  { value: 'amenity', label: 'Amenity Image', description: 'Community amenity photos (4:3)' },
] as const;

export default function AdminImageOptimizer() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageType, setImageType] = useState<string>('hero');
  const [unitId, setUnitId] = useState<string>('site-images');
  const [imageName, setImageName] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<OptimizationResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localOptimizerUrl, setLocalOptimizerUrl] = useState<string>('http://localhost:3001');
  const [optimizerStatus, setOptimizerStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const { toast } = useToast();

  // Check if local optimizer is running
  useEffect(() => {
    const checkOptimizer = async () => {
      try {
        // Add timestamp to prevent caching
        const response = await fetch(`${localOptimizerUrl}/health?t=${Date.now()}`);
        if (response.ok) {
          const data = await response.json();
          setOptimizerStatus('online');
          console.log('Local optimizer status:', data);
        } else {
          setOptimizerStatus('offline');
        }
      } catch (error) {
        setOptimizerStatus('offline');
      }
    };

    checkOptimizer();
    const interval = setInterval(checkOptimizer, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [localOptimizerUrl]);

  // Auto-generate image name when file is selected
  useEffect(() => {
    if (selectedFile && !imageName) {
      const baseName = selectedFile.name.replace(/\.[^/.]+$/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      setImageName(baseName || `${imageType}-${Date.now()}`);
    }
  }, [selectedFile, imageType, imageName]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      setSelectedFile(imageFile);
    } else {
      toast({
        title: 'Invalid file',
        description: 'Please drop an image file',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
    } else {
      toast({
        title: 'Invalid file',
        description: 'Please select an image file',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const handleOptimize = useCallback(async () => {
    if (!selectedFile || !imageType || !unitId || !imageName) {
      toast({
        title: 'Missing information',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    if (optimizerStatus !== 'online') {
      toast({
        title: 'Optimizer not available',
        description: 'Please start the local optimizer server first (npm run dev:optimizer)',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    setProgress(0);
    setResult(null);

    try {
      // Create FormData
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('unitId', unitId);
      formData.append('imageType', imageType);
      formData.append('imageName', imageName);

      // Call local optimizer directly
      const response = await fetch(`${localOptimizerUrl}/optimize`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        const errorMessage = errorData.error || `HTTP ${response.status}`;
        const details = errorData.details ? ` (${JSON.stringify(errorData.details)})` : '';
        console.error('Optimization error details:', errorData);
        throw new Error(`${errorMessage}${details}`);
      }

      const data: OptimizationResult = await response.json();
      setResult(data);
      setProgress(100);

      toast({
        title: 'Success',
        description: `Generated ${data.images.length} optimized image variants`,
      });
    } catch (error) {
      console.error('Optimization error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to optimize image';
      toast({
        title: 'Optimization failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  }, [selectedFile, imageType, unitId, imageName, localOptimizerUrl, optimizerStatus, toast]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const totalSize = result ? Object.values(result.sizes).reduce((sum, size) => sum + size, 0) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Image Optimizer</h2>
        <p className="text-muted-foreground">
          Local development utility for optimizing images. Generates multiple formats (AVIF, WebP, JPEG) and sizes.
        </p>
      </div>

      {/* Optimizer Status */}
      <Alert>
        <Server className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between">
          <span>
            Local Optimizer Status:{' '}
            {optimizerStatus === 'checking' && <span className="text-muted-foreground">Checking...</span>}
            {optimizerStatus === 'online' && <span className="text-green-600 font-semibold">Online</span>}
            {optimizerStatus === 'offline' && <span className="text-red-600 font-semibold">Offline</span>}
          </span>
          <div className="flex items-center gap-2">
            <Input
              type="text"
              value={localOptimizerUrl}
              onChange={(e) => setLocalOptimizerUrl(e.target.value)}
              className="w-64 h-8 text-sm"
              placeholder="http://localhost:3001"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`${localOptimizerUrl}/health`, '_blank')}
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Health Check
            </Button>
          </div>
        </AlertDescription>
      </Alert>

      {optimizerStatus === 'offline' && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Local optimizer is not running.</strong> Start it with:{' '}
            <code className="bg-muted px-1 py-0.5 rounded text-xs">npm run dev:optimizer</code>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle>Upload & Configure</CardTitle>
            <CardDescription>
              Select an image and configure optimization settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Image Type */}
            <div className="space-y-2">
              <Label htmlFor="imageType">Image Type</Label>
              <Select value={imageType} onValueChange={setImageType}>
                <SelectTrigger id="imageType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IMAGE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div>
                        <div className="font-medium">{type.label}</div>
                        <div className="text-xs text-muted-foreground">{type.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Unit ID */}
            <div className="space-y-2">
              <Label htmlFor="unitId">Unit ID</Label>
              <Input
                id="unitId"
                value={unitId}
                onChange={(e) => setUnitId(e.target.value)}
                placeholder="site-images or unit-a-2-5"
              />
              <p className="text-xs text-muted-foreground">
                Use &quot;site-images&quot; for site-wide images (hero, stock, amenity)
              </p>
            </div>

            {/* Image Name */}
            <div className="space-y-2">
              <Label htmlFor="imageName">Image Name</Label>
              <Input
                id="imageName"
                value={imageName}
                onChange={(e) => setImageName(e.target.value)}
                placeholder="hero-1234567890"
              />
              <p className="text-xs text-muted-foreground">
                Base name for generated files (e.g., &quot;hero-1234567890&quot;)
              </p>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>Image File</Label>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging
                    ? 'border-primary bg-primary/5'
                    : 'border-muted-foreground/25 hover:border-muted-foreground/50'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {selectedFile ? (
                  <div className="space-y-2">
                    <FileImage className="h-12 w-12 mx-auto text-muted-foreground" />
                    <div>
                      <p className="font-medium">{selectedFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(selectedFile.size)} • {selectedFile.type}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedFile(null)}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                    <div>
                      <Label htmlFor="file-upload" className="cursor-pointer">
                        <span className="text-primary hover:underline">Click to upload</span>
                        {' '}or drag and drop
                      </Label>
                      <Input
                        id="file-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      PNG, JPG, WebP, AVIF up to 200MB (for large architectural renders)
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Optimize Button */}
            <Button
              onClick={handleOptimize}
              disabled={!selectedFile || uploading || optimizerStatus !== 'online'}
              className="w-full"
              size="lg"
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Optimizing...
                </>
              ) : (
                <>
                  <ImageIcon className="mr-2 h-4 w-4" />
                  Optimize Image
                </>
              )}
            </Button>

            {uploading && (
              <Progress value={progress} className="w-full" />
            )}
          </CardContent>
        </Card>

        {/* Results Section */}
        <Card>
          <CardHeader>
            <CardTitle>Results</CardTitle>
            <CardDescription>
              Optimized image variants and file information
            </CardDescription>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-4">
                <Alert>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription>
                    <strong>Success!</strong> {result.message}
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Total Files:</span>
                    <Badge>{result.images.length}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Total Size:</span>
                    <Badge variant="secondary">{formatFileSize(totalSize)}</Badge>
                  </div>
                </div>

                <Tabs defaultValue="list" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="list">List View</TabsTrigger>
                    <TabsTrigger value="preview">Preview</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="list" className="space-y-2">
                    <div className="max-h-96 overflow-y-auto space-y-2">
                      {result.images.map((img, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 border rounded text-sm"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{img.path}</p>
                            <p className="text-xs text-muted-foreground">
                              {img.width}px • {img.format.toUpperCase()} • {formatFileSize(img.size)}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(img.url, '_blank')}
                            className="ml-2"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="preview" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                      {result.images
                        .filter(img => img.format === 'jpeg')
                        .map((img, index) => (
                          <div key={index} className="space-y-2">
                            <div className="aspect-video bg-muted rounded overflow-hidden">
                              <img
                                src={img.url}
                                alt={`${img.width}px ${img.format}`}
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                  // Fallback if image fails to load
                                  e.currentTarget.style.display = 'none';
                                  const parent = e.currentTarget.parentElement;
                                  if (parent) {
                                    parent.innerHTML = `
                                      <div class="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                                        Failed to load image
                                        <br />
                                        ${img.path.split('/').pop()}
                                      </div>
                                    `;
                                  }
                                }}
                              />
                            </div>
                            <p className="text-xs text-center text-muted-foreground">
                              {img.width}px • {formatFileSize(img.size)}
                            </p>
                          </div>
                        ))}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <ImageIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No results yet. Upload and optimize an image to see results here.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Output Directory Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label className="text-sm font-medium">Output Directory</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Images are saved to: <code className="bg-muted px-1 py-0.5 rounded text-xs">
                  {import.meta.env.VITE_OUTPUT_DIR || './output/images'}/{unitId}/{imageType}/
                </code>
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium">Operation Mode</Label>
              <div className="text-sm text-muted-foreground mt-1">
                Mode: <Badge variant="outline">File Output</Badge> (saves to local directory)
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Set <code className="bg-muted px-1 py-0.5 rounded">SUPABASE_URL</code> and{' '}
                <code className="bg-muted px-1 py-0.5 rounded">SUPABASE_SERVICE_ROLE_KEY</code> for upload mode
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

